import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  loadPromptBuffer,
  savePromptBuffer,
  clearPromptBuffer,
  PROMPT_BUFFER_SCOPES,
} from '../utils/promptBufferStorage';

const DEFAULT_DEBOUNCE_MS = 700;

export default function usePromptBuffer({
  scope = PROMPT_BUFFER_SCOPES.GLOBAL,
  strategyId = null,
  revisionId = null,
  enabled = true,
  debounceMs = DEFAULT_DEBOUNCE_MS,
} = {}) {
  const keyConfig = useMemo(
    () => ({
      scope,
      strategyId: strategyId || null,
      revisionId: revisionId || null,
    }),
    [revisionId, scope, strategyId],
  );

  const [draft, setDraft] = useState(null);
  const [isStorageAvailable, setIsStorageAvailable] = useState(true);
  const timeoutRef = useRef(null);
  const lastSavedValueRef = useRef(null);
  const pendingAttachmentsRef = useRef([]);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) {
      setDraft(null);
      return;
    }

    try {
      const existing = loadPromptBuffer(keyConfig);
      if (mountedRef.current) {
        setDraft(existing);
        setIsStorageAvailable(true);
        if (existing) {
          lastSavedValueRef.current = existing.value;
          pendingAttachmentsRef.current = existing.attachmentsMeta || [];
        }
      }
    } catch (error) {
      if (mountedRef.current) {
        setIsStorageAvailable(false);
      }
    }
  }, [enabled, keyConfig]);

  const cancelPendingSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const clearDraft = useCallback(() => {
    cancelPendingSave();
    try {
      clearPromptBuffer(keyConfig);
      if (mountedRef.current) {
        setDraft(null);
        lastSavedValueRef.current = null;
        pendingAttachmentsRef.current = [];
      }
    } catch (error) {
      // ignore clear failures
    }
  }, [cancelPendingSave, keyConfig]);

  const saveDraft = useCallback(
    (value, attachmentsMeta = []) => {
      if (!enabled || !isStorageAvailable) {
        return;
      }

      cancelPendingSave();

      const normalizedAttachments = Array.isArray(attachmentsMeta) ? attachmentsMeta : [];
      pendingAttachmentsRef.current = normalizedAttachments;
      lastSavedValueRef.current = value;

      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;

        try {
          savePromptBuffer(keyConfig, {
            value,
            attachmentsMeta: normalizedAttachments,
          });

          if (mountedRef.current) {
            if (value && value.trim()) {
              setDraft({
                value,
                attachmentsMeta: normalizedAttachments,
                lastUpdated: Date.now(),
              });
            } else {
              setDraft(null);
            }
          }
        } catch (error) {
          if (mountedRef.current) {
            setIsStorageAvailable(false);
          }
        }
      }, debounceMs);
    },
    [cancelPendingSave, debounceMs, enabled, isStorageAvailable, keyConfig],
  );

  const flushDraft = useCallback(() => {
    if (!timeoutRef.current) return;
    const pendingValue = lastSavedValueRef.current;
    const pendingAttachments = pendingAttachmentsRef.current || [];
    cancelPendingSave();
    if (pendingValue !== null && pendingValue !== undefined) {
      try {
        savePromptBuffer(keyConfig, {
          value: pendingValue,
          attachmentsMeta: pendingAttachments,
        });
      } catch (error) {
        if (mountedRef.current) {
          setIsStorageAvailable(false);
        }
      }
    }
  }, [cancelPendingSave, draft?.attachmentsMeta, keyConfig]);

  useEffect(() => () => cancelPendingSave(), [cancelPendingSave]);

  return {
    draft,
    saveDraft,
    clearDraft,
    flushDraft,
    scope,
    isStorageAvailable,
  };
}

export { PROMPT_BUFFER_SCOPES };
